import { forwardRef, useImperativeHandle } from "react";

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Text,
  VStack
} from "@chakra-ui/react";
import type { PhCalculationRequest } from "@henophess/types";
import { DosageUnits, SupplementType } from "@henophess/types";
import { useForm } from "react-hook-form";

interface PhFormData {
  salivaPh: string;
  urinePh: string;
  alkalineCalciumDosage: string;
  acidicCalciumDosage: string;
  b12Dosage: string;
  bakingSodaDosage: string;
  ionicIronDosage: string;
  testingDate: string;
  testingTime: string;
}

interface PhCalculationInputFormProps {
  onSubmit: (data: PhCalculationRequest) => Promise<void>;
  onSave?: (data: PhCalculationRequest) => Promise<void>;
  isLoading?: boolean;
  isSaving?: boolean;
}

export interface PhCalculationInputFormRef {
  populate: (data: PhCalculationRequest) => void;
}

const RequiredFormLabel: React.FC<{ labelText: string }> = ({ labelText }) => (
  <FormLabel fontSize="sm" mb={1}>
    {labelText}{" "}
    <Text as="span" color="red.500">
      *
    </Text>
  </FormLabel>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Heading size="sm" mb={3} color="green.600">
    {children}
  </Heading>
);

export const PhCalculationInputForm = forwardRef<PhCalculationInputFormRef, PhCalculationInputFormProps>(
  ({ onSubmit, onSave, isLoading = false, isSaving = false }, ref) => {
    const {
      register,
      handleSubmit,
      setValue,
      formState: { errors, isSubmitting }
    } = useForm<PhFormData>();

    const formatFormData = (data: PhFormData): PhCalculationRequest => {
      // Combine date and time for the testing date
      let testingDateTime: string;
      if (data.testingDate) {
        // If date is provided, combine with time or use current time
        const dateStr = data.testingDate;
        const timeStr = data.testingTime || new Date().toTimeString().substring(0, 5);
        const [hours, minutes] = timeStr.split(":");

        // Create a new Date object with the combined date and time
        const testDate = new Date(dateStr);
        testDate.setHours(parseInt(hours), parseInt(minutes));
        testingDateTime = testDate.toISOString();
      } else {
        // If no date provided, use current date and time
        testingDateTime = new Date().toISOString();
      }

      const formattedData: PhCalculationRequest = {
        salivaPh: Number(Number(data.salivaPh).toFixed(2)),
        urinePh: Number(Number(data.urinePh).toFixed(2)),
        testingDate: testingDateTime
      };

      // Handle alkaline calcium
      if (data.alkalineCalciumDosage) {
        formattedData.currentAlkalineCalciumDosage = {
          supplement: SupplementType.AlkalineCalcium,
          amount: Number(data.alkalineCalciumDosage),
          dosageUnit: DosageUnits.Pills
        };
      }

      // Handle acidic calcium
      if (data.acidicCalciumDosage) {
        formattedData.currentAcidicCalciumDosage = {
          supplement: SupplementType.AcidicCalcium,
          amount: Number(data.acidicCalciumDosage),
          dosageUnit: DosageUnits.Pills
        };
      }

      // Handle vitamin B12
      if (data.b12Dosage) {
        formattedData.currentVitaminB12 = {
          supplement: SupplementType.VitaminB12,
          amount: Number(data.b12Dosage)
        };
      }

      // Handle baking soda
      if (data.bakingSodaDosage) {
        formattedData.currentBakingSodaDosage = {
          supplement: SupplementType.BakingSoda,
          amount: Number(data.bakingSodaDosage)
        };
      }

      // Handle ionic iron
      if (data.ionicIronDosage) {
        formattedData.currentIronDosage = {
          supplement: SupplementType.IonicIron,
          amount: Number(data.ionicIronDosage)
        };
      }

      return formattedData;
    };

    const onFormSubmit = (data: PhFormData) => {
      const formattedData = formatFormData(data);
      return onSubmit(formattedData);
    };

    useImperativeHandle(
      ref,
      () => ({
        populate: (data: PhCalculationRequest) => {
          setValue("salivaPh", data.salivaPh.toString());
          setValue("urinePh", data.urinePh.toString());
          if (data.testingDate) {
            const testDate = new Date(data.testingDate);
            setValue("testingDate", testDate.toISOString().split("T")[0]);
            // Format time as HH:MM for time input
            const hours = testDate.getHours().toString().padStart(2, "0");
            const minutes = testDate.getMinutes().toString().padStart(2, "0");
            setValue("testingTime", `${hours}:${minutes}`);
          } else {
            const now = new Date();
            setValue("testingDate", now.toISOString().split("T")[0]);
            const hours = now.getHours().toString().padStart(2, "0");
            const minutes = now.getMinutes().toString().padStart(2, "0");
            setValue("testingTime", `${hours}:${minutes}`);
          }
        }
      }),
      [setValue]
    );

    return (
      <Box
        borderWidth="1px"
        borderRadius="lg"
        p={3}
        width="100%"
        maxWidth="800px"
        ml="0"
        mr="auto"
        borderColor="green.600"
        boxShadow="sm"
      >
        <form onSubmit={handleSubmit(onFormSubmit)} style={{ width: "100%" }}>
          <Box maxW="700px" ml="0" mr="auto">
            <Grid
              templateColumns={{ base: "1fr", md: "0.8fr 1fr 1fr" }}
              gap={1}
              width="100%"
              justifyContent="start"
              alignItems="start"
            >
              {/* Left Column - pH Measurements */}
              <GridItem>
                <VStack spacing={2} align="start">
                  <SectionHeading>pH Measurements</SectionHeading>
                  <FormControl isInvalid={!!errors.salivaPh}>
                    <Heading size="sm" mb={1} color="green.500">
                      Saliva pH
                    </Heading>
                    <Input
                      size="sm"
                      maxW="100px"
                      type="number"
                      step="0.01"
                      {...register("salivaPh", { required: true, min: 0, max: 14 })}
                      isDisabled={isSaving}
                    />
                    <FormErrorMessage>Please enter a valid pH value (0-14)</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.urinePh}>
                    <Heading size="sm" mb={1} color="green.500">
                      Urine pH
                    </Heading>
                    <Input
                      size="sm"
                      maxW="100px"
                      type="number"
                      step="0.01"
                      {...register("urinePh", { required: true, min: 0, max: 14 })}
                      isDisabled={isSaving}
                    />
                    <FormErrorMessage>Please enter a valid pH value (0-14)</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <Heading size="sm" mb={1} color="green.500">
                      Test Date & Time
                    </Heading>
                    <Flex>
                      <Input
                        size="sm"
                        type="date"
                        {...register("testingDate")}
                        defaultValue={new Date().toISOString().split("T")[0]}
                        mr={2}
                        isDisabled={isSaving}
                      />
                      <Input
                        size="sm"
                        type="time"
                        {...register("testingTime")}
                        defaultValue={new Date().toTimeString().substring(0, 5)}
                        isDisabled={isSaving}
                      />
                    </Flex>
                  </FormControl>
                </VStack>
              </GridItem>

              {/* Middle Column - Calcium Supplements */}
              <GridItem>
                <VStack spacing={2} align="start">
                  <Box width="100%">
                    <SectionHeading>Calcium Supplements</SectionHeading>
                  </Box>
                  <FormControl mb={2}>
                    <Heading size="sm" mb={1} color="green.500">
                      Alkaline Calcium{" "}
                      <Text as="span" fontSize="xs" color="green.500">
                        (pills/day)
                      </Text>
                    </Heading>
                    <Input
                      size="sm"
                      maxW="100px"
                      type="number"
                      step="1"
                      placeholder="0"
                      {...register("alkalineCalciumDosage")}
                      isDisabled={isSaving}
                    />
                  </FormControl>

                  <FormControl mb={2}>
                    <Heading size="sm" mb={1} color="green.500">
                      Acidic Calcium{" "}
                      <Text as="span" fontSize="xs" color="green.500">
                        (pills/day)
                      </Text>
                    </Heading>
                    <Input
                      size="sm"
                      maxW="100px"
                      type="number"
                      step="1"
                      placeholder="0"
                      {...register("acidicCalciumDosage")}
                      isDisabled={isSaving}
                    />
                  </FormControl>
                </VStack>
              </GridItem>

              {/* Right Column - Other Supplements */}
              <GridItem>
                <VStack spacing={2} align="start">
                  <Box width="100%">
                    <SectionHeading>Other Supplements</SectionHeading>
                  </Box>
                  <FormControl mb={2}>
                    <Heading size="sm" mb={1} color="green.500">
                      Vitamin B12{" "}
                      <Text as="span" fontSize="xs" color="green.500">
                        (mcg/day)
                      </Text>
                    </Heading>
                    <Input
                      size="sm"
                      maxW="100px"
                      type="number"
                      step="1"
                      placeholder="0"
                      {...register("b12Dosage")}
                      isDisabled={isSaving}
                    />
                  </FormControl>

                  <FormControl mb={2}>
                    <Heading size="sm" mb={1} color="green.500">
                      Ionic Iron{" "}
                      <Text as="span" fontSize="xs" color="green.500">
                        (drops/day)
                      </Text>
                    </Heading>
                    <Input
                      size="sm"
                      maxW="100px"
                      type="number"
                      step="1"
                      placeholder="0"
                      {...register("ionicIronDosage")}
                      isDisabled={isSaving}
                    />
                  </FormControl>

                  <FormControl mb={2}>
                    <Heading size="sm" mb={1} color="green.500">
                      Baking Soda{" "}
                      <Text as="span" fontSize="xs" color="green.500">
                        (tsp/day)
                      </Text>
                    </Heading>
                    <Input
                      size="sm"
                      maxW="100px"
                      type="number"
                      step="0.25"
                      placeholder="0"
                      {...register("bakingSodaDosage")}
                      isDisabled={isSaving}
                    />
                  </FormControl>
                </VStack>
              </GridItem>
            </Grid>
          </Box>

          <Flex justify="flex-start" mt={6} gap={4}>
            <Button
              colorScheme="green"
              size="sm"
              type="submit"
              isLoading={isSubmitting || isLoading}
              isDisabled={isSaving}
              loadingText="Calculating"
            >
              Calculate
            </Button>
            {onSave && (
              <Button
                colorScheme="blue"
                size="sm"
                isLoading={isLoading || isSaving}
                loadingText="Saving"
                isDisabled={isSubmitting}
                onClick={handleSubmit((data) => {
                  const formattedData = formatFormData(data);
                  return onSave(formattedData);
                })}
              >
                Save
              </Button>
            )}
          </Flex>
        </form>
      </Box>
    );
  }
);
